import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Brand from "../components/Brand";
import PinPad from "../components/PinPad";

export default function KioskLogin() {
  const [step, setStep] = useState("code");
  const [code, setCode] = useState(() => localStorage.getItem("mep_kiosk_code") || "");
  const [saveCode, setSaveCode] = useState(() => !!localStorage.getItem("mep_kiosk_code"));
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const { saveSession } = useAuth();
  const navigate = useNavigate();

  const findRestaurant = async (e) => {
    e?.preventDefault();
    if (code.trim().length < 4) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/auth/restaurant/${code.trim().toUpperCase()}`);
      if (saveCode) {
        localStorage.setItem("mep_kiosk_code", code.trim().toUpperCase());
      } else {
        localStorage.removeItem("mep_kiosk_code");
      }
      setData(data);
      setStep("staff");
    } catch (err) {
      toast.error(formatApiError(err, "Business not found"));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (step !== "pin" || pin.length !== 4 || !selected) return;
    (async () => {
      setBusy(true);
      try {
        const { data: session } = await api.post("/auth/pin-login", {
          code: code.trim().toUpperCase(),
          user_id: selected.id,
          pin,
        });
        saveSession(session);
        toast.success(`On the line, ${session.user.name}!`);
        navigate("/app");
      } catch (err) {
        toast.error(formatApiError(err, "Incorrect PIN"));
        setPin("");
      } finally {
        setBusy(false);
      }
    })();
  }, [pin, step, selected, code, navigate, saveSession]);

  return (
    <div className="min-h-screen bg-[#FBF8F3] flex flex-col items-center justify-center px-4 py-10">
      <Link to="/" className="flex items-center gap-2 mb-8" data-testid="kiosk-brand-link">
        <Brand size="text-xl" iconClass="h-10 w-10" />
      </Link>

      <div className="w-full max-w-md p-6 sm:p-8 rounded-2xl bg-white border border-[#E4DDD3]">
        {step === "code" && (
          <>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1A1D20]">Staff Kiosk</h1>
            <p className="text-sm text-slate-500 mt-1 mb-6">Enter your 6-character business code</p>
            <form onSubmit={findRestaurant} className="space-y-4">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))}
                placeholder="e.g. DEMO24"
                autoFocus
                data-testid="restaurant-code-input"
                className="w-full h-16 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-center text-3xl font-mono font-bold tracking-[0.4em] text-terra-600 placeholder-slate-400 placeholder-tracking-normal focus:outline-none focus:border-terra-400/60 uppercase"
              />
              <button
                type="submit"
                disabled={busy || code.length < 4}
                data-testid="restaurant-code-submit-button"
                className="w-full h-14 rounded-xl bg-terra-500 text-white font-display font-bold text-lg hover:bg-terra-600 disabled:opacity-50 transition-colors"
              >
                {busy ? "Checking…" : "Find Business"}
              </button>
              <label className="flex items-center gap-2.5 text-sm text-slate-500 cursor-pointer select-none" data-testid="save-code-toggle">
                <input
                  type="checkbox"
                  checked={saveCode}
                  onChange={(e) => setSaveCode(e.target.checked)}
                  className="w-4 h-4 accent-terra-500"
                  data-testid="save-code-checkbox"
                />
                Save this code on this device
              </label>
            </form>
            <div className="mt-6 text-sm text-slate-500 text-center">
              First shift?{" "}
              <Link to={code.trim() ? `/join?code=${code.trim().toUpperCase()}` : "/join"} className="text-terra-600 hover:underline" data-testid="goto-join-link">
                Join your business
              </Link>
            </div>
          </>
        )}

        {step === "staff" && data && (
          <>
            <button onClick={() => setStep("code")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-terra-600 mb-4" data-testid="kiosk-back-button">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            {data.restaurant.logo_path && (
              <img
                src={`/api/public/logo/${code.trim().toUpperCase()}`}
                alt={data.restaurant.name}
                className="h-16 w-auto max-w-[200px] object-contain rounded-xl mb-3"
                data-testid="kiosk-business-logo"
              />
            )}
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1A1D20]" data-testid="kiosk-restaurant-name">{data.restaurant.name}</h1>
            <p className="text-sm text-slate-500 mt-1 mb-6">Who's on shift?</p>
            {data.staff.length === 0 ? (
              <div className="text-slate-500 text-sm" data-testid="kiosk-no-staff">
                No staff registered yet.{" "}
                <Link to={`/join?code=${code.trim().toUpperCase()}`} className="text-terra-600 hover:underline" data-testid="kiosk-join-link">Join now</Link>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  {data.staff.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => { setSelected(s); setPin(""); setStep("pin"); }}
                      data-testid={`kiosk-staff-button-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className="p-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] hover:border-terra-400/50 active:scale-95 transition-all text-left"
                    >
                      <div className="font-medium text-[#1A1D20]">{s.name}</div>
                      <div className="text-xs text-terra-600 mt-1 uppercase tracking-wider">{s.primary_role}</div>
                    </button>
                  ))}
                </div>
                <div className="mt-6 text-sm text-slate-500 text-center">
                  Not on the list?{" "}
                  <Link to={`/join?code=${code.trim().toUpperCase()}`} className="text-terra-600 hover:underline" data-testid="kiosk-join-link">
                    Join this business
                  </Link>
                </div>
              </>
            )}
          </>
        )}

        {step === "pin" && selected && (
          <>
            <button onClick={() => setStep("staff")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-terra-600 mb-4" data-testid="pin-back-button">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1A1D20]" data-testid="pin-staff-name">{selected.name}</h1>
            <p className="text-sm text-slate-500 mt-1 mb-6">Enter your 4-digit PIN</p>
            <PinPad value={pin} onChange={setPin} disabled={busy} />
          </>
        )}
      </div>
    </div>
  );
}
